/**
 * InlineInvoiceMessage Component
 *
 * A payment form rendered inline within the chat interface.
 * Supports one-click payment with saved methods, Apple Pay, Google Pay,
 * and new card/bank account entry via Stripe Elements.
 *
 * UX Priority: Fewest possible steps for the user.
 */

import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { CreditCard, Building2, ChevronDown, Check, X, Loader2, AlertCircle, Wallet } from 'lucide-react';
import { usePaymentGateListener, type PaymentStatus, type WorkflowPaymentData } from '../../hooks/usePaymentGateListener';

// ============================================================================
// Types
// ============================================================================

export interface SavedPaymentMethod {
  id: string;
  type: 'card' | 'bank';
  brand?: string;
  last4: string;
  expMonth?: number;
  expYear?: number;
  bankName?: string;
  accountType?: string;
  isDefault: boolean;
}

export interface InlineInvoiceMessageProps {
  /** Amount to charge in USD */
  amountUsd: number;
  /** Invoice description shown to user */
  description: string;
  /** Invoice title/header */
  invoiceTitle?: string;
  /** Stripe PaymentIntent client secret */
  clientSecret: string;
  /** Stripe PaymentIntent ID (also Firestore doc ID) */
  paymentIntentId: string;
  /** User ID being charged */
  targetUid: string;
  /** Allowed payment methods */
  allowedMethods: ('card' | 'bank' | 'ach')[];
  /** Pre-fetched saved payment methods */
  savedMethods?: {
    cards: Array<{ id: string; brand: string; last4: string; expMonth: number; expYear: number; isDefault: boolean }>;
    bankAccounts: Array<{ id: string; bankName: string; last4: string; accountType: string; isDefault: boolean }>;
  };
  /** Called when payment succeeds */
  onPaymentComplete?: (result: { paymentIntentId: string; paymentMethod: string; amountPaid: number }) => void;
  /** Called when payment fails */
  onPaymentFailed?: (error: string) => void;
  /** Called when user cancels */
  onCancel?: () => void;
  /** Stripe publishable key (optional, will use env var if not provided) */
  stripePublishableKey?: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const checkmark = keyframes`
  0% { stroke-dashoffset: 50; }
  100% { stroke-dashoffset: 0; }
`;

const Container = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  max-width: 440px;
  animation: ${fadeIn} 0.3s ease-out;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const TitleSection = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
`;

const Amount = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #059669;
  margin-top: 2px;
`;

const Description = styled.p`
  margin: 0 0 20px;
  font-size: 14px;
  color: #64748b;
  line-height: 1.5;
`;

const Divider = styled.div<{ $hasText?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 16px 0;
  color: #94a3b8;
  font-size: 12px;
  text-transform: lowercase;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }

  ${props => !props.$hasText && css`
    &::before,
    &::after {
      content: none;
    }
    background: #e2e8f0;
    height: 1px;
  `}
`;

const SavedMethodsSection = styled.div`
  margin-bottom: 16px;
`;

const SavedMethodButton = styled.button<{ $isSelected?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: ${props => props.$isSelected ? '#ecfdf5' : 'white'};
  border: 2px solid ${props => props.$isSelected ? '#059669' : '#e2e8f0'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  margin-bottom: 8px;

  &:hover {
    border-color: ${props => props.$isSelected ? '#059669' : '#cbd5e1'};
    background: ${props => props.$isSelected ? '#ecfdf5' : '#f8fafc'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const MethodIcon = styled.div<{ $type: 'card' | 'bank' }>`
  width: 36px;
  height: 36px;
  background: ${props => props.$type === 'card' ? 'rgba(198, 167, 94, 0.16)' : '#fef3c7'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$type === 'card' ? '#8A6D2F' : '#f59e0b'};
`;

const MethodDetails = styled.div`
  flex: 1;
  text-align: left;
`;

const MethodLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
`;

const MethodSubtitle = styled.div`
  font-size: 12px;
  color: #64748b;
`;

const DefaultBadge = styled.span`
  font-size: 10px;
  background: #059669;
  color: white;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: lowercase;
  font-weight: 600;
`;

const PayButton = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  width: 100%;
  padding: 14px 20px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  ${props => props.$variant === 'secondary' ? css`
    background: #f1f5f9;
    color: #475569;

    &:hover:not(:disabled) {
      background: #e2e8f0;
    }
  ` : css`
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    color: white;
    box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);

    &:hover:not(:disabled) {
      box-shadow: 0 4px 12px rgba(5, 150, 105, 0.4);
      transform: translateY(-1px);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const NewMethodSection = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const ExpandButton = styled.button`
  width: 100%;
  padding: 12px;
  background: transparent;
  border: 1px dashed #cbd5e1;
  border-radius: 10px;
  color: #64748b;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.15s ease;

  &:hover {
    border-color: #94a3b8;
    color: #475569;
    background: #f8fafc;
  }

  svg {
    transition: transform 0.3s ease;
  }

  &[data-expanded='true'] svg {
    transform: rotate(180deg);
  }
`;

const StripeElementWrapper = styled.div`
  padding: 16px 0;

  .StripeElement {
    padding: 14px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    background: white;
    /* Never wider than the invoice card (docked-mode UX, 2026-07-13): the
       docked pane's tile interior is ~228px, and a hard 280px minimum made
       the card-entry field bleed past the invoice card's rounded border
       exactly where the user must type card details. */
    min-width: min(280px, 100%);
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:focus-within {
      border-color: #059669;
      box-shadow: 0 0 0 3px rgba(5, 150, 105, 0.1);
    }
  }
`;

const StatusMessage = styled.div<{ $type: 'error' | 'success' | 'processing' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  margin-top: 12px;

  ${props => {
    switch (props.$type) {
      case 'error':
        return css`
          background: #fef2f2;
          color: #dc2626;
          border: 1px solid #fecaca;
        `;
      case 'success':
        return css`
          background: #ecfdf5;
          color: #059669;
          border: 1px solid #a7f3d0;
        `;
      case 'processing':
        return css`
          background: #f0f9ff;
          color: #0284c7;
          border: 1px solid #bae6fd;
        `;
    }
  }}
`;

const SpinnerIcon = styled(Loader2)`
  animation: ${spin} 1s linear infinite;
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 20px;
  animation: ${fadeIn} 0.3s ease-out;
`;

const SuccessIcon = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #059669 0%, #10b981 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: white;

  svg {
    width: 32px;
    height: 32px;
  }
`;

const SuccessTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
`;

const SuccessMessage = styled.p`
  margin: 0;
  font-size: 14px;
  color: #64748b;
`;

const CancelButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #94a3b8;
  border-radius: 4px;
  transition: all 0.15s ease;

  &:hover {
    color: #64748b;
    background: #f1f5f9;
  }
`;

const ContainerRelative = styled(Container)`
  position: relative;
`;

// ============================================================================
// Stripe Promise
// ============================================================================

let stripePromise: ReturnType<typeof loadStripe> | null = null;

function getStripePromise(publishableKey?: string): ReturnType<typeof loadStripe> {
  if (!stripePromise) {
    const key = publishableKey
      || process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
      || (window as any).__PUBLIC_STRIPE_KEY__
      || '';

    if (!key || !key.startsWith('pk_')) {
      console.warn('Stripe publishable key not configured');
      return Promise.resolve(null);
    }

    stripePromise = loadStripe(key);
  }
  return stripePromise!;
}

// ============================================================================
// Payment Form Inner Component (requires Elements context)
// ============================================================================

interface PaymentFormInnerProps {
  amountUsd: number;
  clientSecret: string;
  savedMethods: SavedPaymentMethod[];
  allowedMethods: ('card' | 'bank' | 'ach')[];
  onPaymentComplete: (result: { paymentIntentId: string; paymentMethod: string; amountPaid: number }) => void;
  onPaymentFailed: (error: string) => void;
  paymentStatus: PaymentStatus;
  paymentData: WorkflowPaymentData | null;
}

const PaymentFormInner: React.FC<PaymentFormInnerProps> = ({
  amountUsd,
  clientSecret,
  savedMethods,
  allowedMethods,
  onPaymentComplete,
  onPaymentFailed,
  paymentStatus,
  paymentData,
}) => {
  const stripe = useStripe();
  const elements = useElements();

  const [selectedMethod, setSelectedMethod] = useState<string | null>(
    savedMethods.find(m => m.isDefault)?.id || savedMethods[0]?.id || null
  );
  const [showNewMethodForm, setShowNewMethodForm] = useState(savedMethods.length === 0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Handle payment with saved method
  const handlePayWithSavedMethod = useCallback(async () => {
    if (!stripe || !selectedMethod) return;

    setIsProcessing(true);
    setError(null);

    try {
      const { error: confirmError } = await stripe.confirmPayment({
        clientSecret,
        confirmParams: {
          payment_method: selectedMethod,
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment failed');
        onPaymentFailed(confirmError.message || 'Payment failed');
      }
      // Success is handled via webhook -> Firestore -> usePaymentGateListener
    } catch (e: any) {
      const msg = e?.message || 'Payment failed';
      setError(msg);
      onPaymentFailed(msg);
    } finally {
      setIsProcessing(false);
    }
  }, [stripe, selectedMethod, clientSecret, onPaymentFailed]);

  // Handle payment with new method (via CardElement)
  const handlePayWithNewMethod = useCallback(async () => {
    if (!stripe || !elements) return;

    const cardElement = elements.getElement(CardElement);
    if (!cardElement) return;

    setIsProcessing(true);
    setError(null);

    try {
      const { error: confirmError } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (confirmError) {
        setError(confirmError.message || 'Payment failed');
        onPaymentFailed(confirmError.message || 'Payment failed');
      }
      // Success is handled via webhook -> Firestore -> usePaymentGateListener
    } catch (e: any) {
      const msg = e?.message || 'Payment failed';
      setError(msg);
      onPaymentFailed(msg);
    } finally {
      setIsProcessing(false);
    }
  }, [stripe, elements, clientSecret, onPaymentFailed]);

  // React to payment status changes from Firestore listener
  useEffect(() => {
    if (paymentStatus === 'succeeded' && paymentData) {
      onPaymentComplete({
        paymentIntentId: paymentData.paymentIntentId,
        paymentMethod: paymentData.paymentMethod || 'card',
        amountPaid: paymentData.amountUsd,
      });
    } else if (paymentStatus === 'failed' && paymentData) {
      setError(paymentData.failureMessage || 'Payment failed');
    }
  }, [paymentStatus, paymentData, onPaymentComplete]);

  // If payment succeeded, show success state
  if (paymentStatus === 'succeeded') {
    return (
      <SuccessContainer>
        <SuccessIcon>
          <Check />
        </SuccessIcon>
        <SuccessTitle>Payment Successful</SuccessTitle>
        <SuccessMessage>
          ${amountUsd.toFixed(2)} has been charged successfully.
        </SuccessMessage>
      </SuccessContainer>
    );
  }

  const hasSavedMethods = savedMethods.length > 0;

  return (
    <>
      {/* Saved Payment Methods */}
      {hasSavedMethods && (
        <SavedMethodsSection>
          {savedMethods.map((method) => (
            <SavedMethodButton
              key={method.id}
              $isSelected={selectedMethod === method.id}
              onClick={() => {
                setSelectedMethod(method.id);
                setShowNewMethodForm(false);
              }}
              disabled={isProcessing}
            >
              <MethodIcon $type={method.type}>
                {method.type === 'card' ? <CreditCard size={18} /> : <Building2 size={18} />}
              </MethodIcon>
              <MethodDetails>
                <MethodLabel>
                  {method.type === 'card'
                    ? `${method.brand?.charAt(0).toUpperCase()}${method.brand?.slice(1) || 'Card'} ****${method.last4}`
                    : `${method.bankName || 'Bank'} ****${method.last4}`}
                </MethodLabel>
                <MethodSubtitle>
                  {method.type === 'card' && method.expMonth && method.expYear
                    ? `Expires ${method.expMonth}/${method.expYear.toString().slice(-2)}`
                    : method.accountType || 'Checking'}
                </MethodSubtitle>
              </MethodDetails>
              {method.isDefault && <DefaultBadge>default</DefaultBadge>}
            </SavedMethodButton>
          ))}

          {/* Pay button for saved method */}
          {selectedMethod && !showNewMethodForm && (
            <PayButton
              onClick={handlePayWithSavedMethod}
              disabled={isProcessing || !stripe}
            >
              {isProcessing ? (
                <>
                  <SpinnerIcon size={18} />
                  Processing...
                </>
              ) : (
                <>
                  Pay ${amountUsd.toFixed(2)}
                </>
              )}
            </PayButton>
          )}

          <Divider $hasText>or use a different method</Divider>
        </SavedMethodsSection>
      )}

      {/* New Payment Method Section */}
      {hasSavedMethods ? (
        <>
          <ExpandButton
            onClick={() => setShowNewMethodForm(!showNewMethodForm)}
            data-expanded={showNewMethodForm}
          >
            <ChevronDown size={16} />
            {showNewMethodForm ? 'hide new payment form' : 'use a new payment method'}
          </ExpandButton>

          <NewMethodSection $isExpanded={showNewMethodForm}>
            <StripeElementWrapper>
              <CardElement
                options={{
                  style: {
                    base: {
                      fontSize: '16px',
                      color: '#1f2937',
                      '::placeholder': {
                        color: '#9ca3af',
                      },
                    },
                    invalid: {
                      color: '#ef4444',
                    },
                  },
                }}
              />
            </StripeElementWrapper>

            <PayButton
              onClick={handlePayWithNewMethod}
              disabled={isProcessing || !stripe || !elements}
            >
              {isProcessing ? (
                <>
                  <SpinnerIcon size={18} />
                  Processing...
                </>
              ) : (
                <>
                  Pay ${amountUsd.toFixed(2)}
                </>
              )}
            </PayButton>
          </NewMethodSection>
        </>
      ) : (
        // No saved methods - show form directly
        <>
          <StripeElementWrapper>
            <CardElement
              options={{
                style: {
                  base: {
                    fontSize: '16px',
                    color: '#1f2937',
                    '::placeholder': {
                      color: '#9ca3af',
                    },
                  },
                  invalid: {
                    color: '#ef4444',
                  },
                },
              }}
            />
          </StripeElementWrapper>

          <PayButton
            onClick={handlePayWithNewMethod}
            disabled={isProcessing || !stripe || !elements}
          >
            {isProcessing ? (
              <>
                <SpinnerIcon size={18} />
                Processing...
              </>
            ) : (
              <>
                Pay ${amountUsd.toFixed(2)}
              </>
            )}
          </PayButton>
        </>
      )}

      {/* Error Message */}
      {error && (
        <StatusMessage $type="error">
          <AlertCircle size={16} />
          {error}
        </StatusMessage>
      )}

      {/* Processing Status */}
      {paymentStatus === 'processing' && (
        <StatusMessage $type="processing">
          <SpinnerIcon size={16} />
          Verifying payment...
        </StatusMessage>
      )}
    </>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const InlineInvoiceMessage: React.FC<InlineInvoiceMessageProps> = ({
  amountUsd,
  description,
  invoiceTitle = 'Payment Required',
  clientSecret,
  paymentIntentId,
  targetUid,
  allowedMethods = ['card', 'bank', 'ach'],
  savedMethods,
  onPaymentComplete,
  onPaymentFailed,
  onCancel,
  stripePublishableKey,
}) => {
  // Set up real-time payment status listener
  const { status: paymentStatus, paymentData, isLoading } = usePaymentGateListener(
    targetUid,
    paymentIntentId,
    {
      onSuccess: (data) => {
        onPaymentComplete?.({
          paymentIntentId: data.paymentIntentId,
          paymentMethod: data.paymentMethod || 'card',
          amountPaid: data.amountUsd,
        });
      },
      onFailed: (data, error) => {
        onPaymentFailed?.(error);
      },
    }
  );

  // Combine saved methods into unified list
  const combinedSavedMethods: SavedPaymentMethod[] = useMemo(() => {
    const methods: SavedPaymentMethod[] = [];

    if (savedMethods?.cards) {
      methods.push(...savedMethods.cards.map(c => ({
        id: c.id,
        type: 'card' as const,
        brand: c.brand,
        last4: c.last4,
        expMonth: c.expMonth,
        expYear: c.expYear,
        isDefault: c.isDefault,
      })));
    }

    if (savedMethods?.bankAccounts && (allowedMethods.includes('bank') || allowedMethods.includes('ach'))) {
      methods.push(...savedMethods.bankAccounts.map(b => ({
        id: b.id,
        type: 'bank' as const,
        last4: b.last4,
        bankName: b.bankName,
        accountType: b.accountType,
        isDefault: b.isDefault,
      })));
    }

    // Sort to put default first
    return methods.sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
  }, [savedMethods, allowedMethods]);

  // Stripe options
  const stripeOptions = useMemo(() => ({
    clientSecret,
    appearance: {
      theme: 'stripe' as const,
      variables: {
        fontFamily: 'inherit',
        colorPrimary: '#059669',
        colorBackground: '#ffffff',
        colorText: '#1e293b',
        colorDanger: '#dc2626',
        borderRadius: '8px',
        spacingUnit: '4px',
      },
    },
  }), [clientSecret]);

  // If payment succeeded, show success state
  if (paymentStatus === 'succeeded') {
    return (
      <ContainerRelative>
        <SuccessContainer>
          <SuccessIcon>
            <Check />
          </SuccessIcon>
          <SuccessTitle>Payment Successful</SuccessTitle>
          <SuccessMessage>
            ${amountUsd.toFixed(2)} has been charged successfully.
          </SuccessMessage>
        </SuccessContainer>
      </ContainerRelative>
    );
  }

  // If cancelled, show nothing or minimal state
  if (paymentStatus === 'cancelled') {
    return (
      <ContainerRelative>
        <StatusMessage $type="error">
          <X size={16} />
          Payment was cancelled
        </StatusMessage>
      </ContainerRelative>
    );
  }

  return (
    <ContainerRelative>
      {onCancel && (
        <CancelButton onClick={onCancel} title="Cancel">
          <X size={18} />
        </CancelButton>
      )}

      <Header>
        <IconWrapper>
          <Wallet size={20} />
        </IconWrapper>
        <TitleSection>
          <Title>{invoiceTitle}</Title>
          <Amount>${amountUsd.toFixed(2)}</Amount>
        </TitleSection>
      </Header>

      {description && <Description>{description}</Description>}

      <Elements stripe={getStripePromise(stripePublishableKey)} options={stripeOptions}>
        <PaymentFormInner
          amountUsd={amountUsd}
          clientSecret={clientSecret}
          savedMethods={combinedSavedMethods}
          allowedMethods={allowedMethods}
          onPaymentComplete={onPaymentComplete || (() => {})}
          onPaymentFailed={onPaymentFailed || (() => {})}
          paymentStatus={paymentStatus}
          paymentData={paymentData}
        />
      </Elements>
    </ContainerRelative>
  );
};

export default InlineInvoiceMessage;
